import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Download,
  ExternalLink,
  ShieldCheck,
  Smartphone,
  Zap,
} from "lucide-react";

import Footer from "./Footer";
import "./DownloadApp.css";

import logo from "../components/Images/icon colored 7.png";

const APK_DOWNLOAD_URL = "/downloads/kostpadi.apk";
const WEBSITE_URL = "https://kostpadi.com";

const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const features = [
  "Create estimates, invoices, and receipts",
  "Track sales, purchases, and payments",
  "Manage customers and suppliers",
  "Monitor your business finances",
  "Access reports and accounting tools",
  "Manage your business wherever you are",
];

const detectDevice = () => {
  if (typeof navigator === "undefined") {
    return "desktop";
  }

  const userAgent = navigator.userAgent || navigator.vendor || "";

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return "ios";
  }

  // iPadOS 13+ can sometimes identify itself as Macintosh.
  if (
    /Macintosh/i.test(userAgent) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  ) {
    return "ios";
  }

  return "desktop";
};

function DownloadApp() {
  const [device, setDevice] = useState("desktop");

  useEffect(() => {
    setDevice(detectDevice());
  }, []);

  const isAndroid = device === "android";
  const isIOS = device === "ios";

  return (
    <div className="download-root">
      {/* HERO */}
      <header className="kp-container download-hero">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="download-hero-grid"
        >
          {/* LEFT */}
          <div className="download-hero-content">
            <motion.span variants={fadeInUp} className="download-tag">
              {isAndroid
                ? "KostPadi for Android"
                : isIOS
                  ? "KostPadi for iPhone & iPad"
                  : "KostPadi Mobile"}
            </motion.span>

            <motion.h1 variants={fadeInUp}>
              Your business finances.
              <span> In your pocket.</span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="download-lead">
              Take KostPadi with you wherever you go. Create invoices, track
              sales and purchases, manage customers, monitor your finances, and
              keep your business moving from your device.
            </motion.p>

            {/* ANDROID */}
            {isAndroid && (
              <motion.div variants={fadeInUp} className="device-download-card">
                <div className="device-download-icon">
                  <Download size={22} />
                </div>

                <div>
                  <strong>Download KostPadi for Android</strong>
                  <p>
                    Install the official KostPadi Android app directly from our
                    website.
                  </p>
                </div>

                <a
                  href={APK_DOWNLOAD_URL}
                  className="download-btn download-btn-primary"
                  download
                >
                  <Download size={18} />
                  Download App
                </a>
              </motion.div>
            )}

            {/* IOS */}
            {isIOS && (
              <motion.div
                variants={fadeInUp}
                className="device-download-card ios-download-card"
              >
                <div className="device-download-icon">
                  <Smartphone size={22} />
                </div>

                <div>
                  <strong>iPhone app coming soon</strong>
                  <p>
                    We're preparing KostPadi for iPhone and iPad. For now, you
                    can access the full KostPadi experience from your browser.
                  </p>
                </div>

                <a
                  href={WEBSITE_URL}
                  className="download-btn download-btn-primary"
                >
                  Open KostPadi
                  <ExternalLink size={18} />
                </a>
              </motion.div>
            )}

            {/* DESKTOP */}
            {!isAndroid && !isIOS && (
              <motion.div
                variants={fadeInUp}
                className="desktop-download-actions"
              >
                <div className="download-hero-actions">
                  <a
                    href={APK_DOWNLOAD_URL}
                    className="download-btn download-btn-primary"
                    download
                  >
                    <Download size={19} />
                    Download Android App
                  </a>

                  <a
                    href={WEBSITE_URL}
                    className="download-btn download-btn-ghost"
                  >
                    Use KostPadi Web
                    <ExternalLink size={18} />
                  </a>
                </div>

                <p className="desktop-download-note">
                  The Android app is currently available for direct download.
                  iPhone and iPad support is coming soon.
                </p>
              </motion.div>
            )}

            <motion.div variants={fadeInUp} className="download-trust-row">
              <div>
                <CheckCircle2 size={17} />
                <span>Official KostPadi app</span>
              </div>

              <div>
                <ShieldCheck size={17} />
                <span>Secure connection</span>
              </div>

              <div>
                <Zap size={17} />
                <span>Built for SMEs</span>
              </div>
            </motion.div>
          </div>

          {/* PHONE MOCKUP */}
          <motion.div variants={fadeInUp} className="download-phone-area">
            <div className="download-glow" />

            <div className="download-phone">
              <div className="download-phone-notch" />

              <div className="download-phone-screen">
                <div className="download-phone-brand">
                  <img src={logo} alt="KostPadi" />
                  <span>KostPadi</span>
                </div>

                <div className="download-phone-welcome">
                  <small>Welcome back</small>
                  <strong>Manage your business</strong>
                </div>

                <div className="download-phone-card">
                  <div className="download-phone-card-icon">₦</div>

                  <div>
                    <small>Total Revenue</small>
                    <strong>₦2,450,000</strong>
                  </div>
                </div>

                <div className="download-phone-grid">
                  <div>
                    <span>Invoices</span>
                    <strong>24</strong>
                  </div>

                  <div>
                    <span>Customers</span>
                    <strong>48</strong>
                  </div>

                  <div>
                    <span>Sales</span>
                    <strong>₦1.8m</strong>
                  </div>

                  <div>
                    <span>Expenses</span>
                    <strong>₦620k</strong>
                  </div>
                </div>

                <div className="download-phone-button">Create Invoice</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </header>

      <main>
        {/* FEATURES */}
        <section className="download-section">
          <div className="kp-container">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="download-section-head"
            >
              <motion.span variants={fadeInUp} className="download-mini-tag">
                Everything you need
              </motion.span>

              <motion.h2 variants={fadeInUp}>
                Your business tools, wherever business happens.
              </motion.h2>

              <motion.p variants={fadeInUp}>
                The KostPadi mobile experience brings the tools you already use
                into a simple, modern experience.
              </motion.p>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="download-feature-grid"
            >
              {features.map((feature) => (
                <motion.div
                  variants={fadeInUp}
                  className="download-feature-card"
                  key={feature}
                >
                  <div className="download-feature-icon">
                    <CheckCircle2 size={19} />
                  </div>

                  <span>{feature}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ANDROID INSTALLATION */}
        {isAndroid && (
          <section
            id="installation"
            className="download-section download-section-alt"
          >
            <div className="kp-container">
              <div className="download-install-grid">
                <div>
                  <span className="download-mini-tag">Getting started</span>

                  <h2>Install KostPadi on your Android phone.</h2>

                  <p>
                    Until KostPadi is available on Google Play, you can install
                    the official Android application directly from our website.
                  </p>

                  <a
                    href={APK_DOWNLOAD_URL}
                    className="download-btn download-btn-primary"
                    download
                  >
                    <Download size={19} />
                    Download KostPadi
                  </a>
                </div>

                <div className="download-steps">
                  <div className="download-step">
                    <div className="download-step-number">01</div>

                    <div>
                      <h3>Download the app</h3>
                      <p>
                        Tap the download button and wait for the APK file to
                        finish downloading.
                      </p>
                    </div>
                  </div>

                  <div className="download-step">
                    <div className="download-step-number">02</div>

                    <div>
                      <h3>Allow installation</h3>
                      <p>
                        Android may ask you to allow your browser to install
                        apps from this source. Enable the permission when
                        prompted.
                      </p>
                    </div>
                  </div>

                  <div className="download-step">
                    <div className="download-step-number">03</div>

                    <div>
                      <h3>Install KostPadi</h3>
                      <p>Open the downloaded APK and tap Install.</p>
                    </div>
                  </div>

                  <div className="download-step">
                    <div className="download-step-number">04</div>

                    <div>
                      <h3>Open and get started</h3>
                      <p>
                        Launch KostPadi, sign in or create your account, and
                        start managing your business.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* IOS */}
        {isIOS && (
          <section className="download-section download-section-alt">
            <div className="kp-container">
              <div className="ios-coming-card">
                <div className="ios-coming-icon">
                  <Smartphone size={28} />
                </div>

                <span className="download-mini-tag">Coming soon</span>

                <h2>KostPadi for iPhone is on the way.</h2>

                <p>
                  We're working toward making KostPadi available on the App
                  Store. In the meantime, you can use KostPadi directly from
                  your browser.
                </p>

                <a
                  href={WEBSITE_URL}
                  className="download-btn download-btn-primary"
                >
                  Open KostPadi Web
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* DESKTOP */}
        {!isAndroid && !isIOS && (
          <section className="download-section download-section-alt">
            <div className="kp-container">
              <div className="desktop-platform-card">
                <div>
                  <span className="download-mini-tag">
                    Choose your experience
                  </span>

                  <h2>KostPadi works wherever your business takes you.</h2>

                  <p>
                    Download the Android app or access KostPadi directly from
                    your browser.
                  </p>
                </div>

                <div className="desktop-platform-actions">
                  <a
                    href={APK_DOWNLOAD_URL}
                    className="download-btn download-btn-primary"
                    download
                  >
                    <Download size={19} />
                    Android App
                  </a>

                  <a
                    href={WEBSITE_URL}
                    className="download-btn download-btn-ghost"
                  >
                    Web App
                    <ExternalLink size={18} />
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SECURITY NOTICE */}
        {isAndroid && (
          <section className="kp-container download-section">
            <div className="download-security-card">
              <div className="download-security-icon">
                <ShieldCheck size={25} />
              </div>

              <div>
                <h3>A quick note about Android installation</h3>

                <p>
                  Because the Android app is currently distributed directly from
                  the KostPadi website rather than Google Play, Android may
                  display a security warning during installation. This is
                  expected for apps installed outside the Play Store.
                </p>

                <p>
                  You should only download KostPadi from{" "}
                  <strong>kostpadi.com</strong>. We are working toward Google
                  Play distribution, which will provide a more seamless
                  installation experience.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section className="download-final">
          <div className="kp-container">
            <div className="download-final-card">
              <div className="download-final-icon">
                {isAndroid ? (
                  <Download size={28} />
                ) : isIOS ? (
                  <Smartphone size={28} />
                ) : (
                  <Smartphone size={28} />
                )}
              </div>

              <h2>
                {isAndroid
                  ? "Take KostPadi with you."
                  : isIOS
                    ? "KostPadi is coming to iPhone."
                    : "Take KostPadi with you."}
              </h2>

              <p>
                {isAndroid
                  ? "Manage your business from wherever you are."
                  : isIOS
                    ? "Use KostPadi on the web while we prepare the iOS app."
                    : "Manage your business from wherever you are."}
              </p>

              {isAndroid ? (
                <a
                  href={APK_DOWNLOAD_URL}
                  className="download-btn download-btn-light"
                  download
                >
                  <Download size={19} />
                  Download the Android App
                </a>
              ) : (
                <a
                  href={WEBSITE_URL}
                  className="download-btn download-btn-light"
                >
                  Open KostPadi
                  <ExternalLink size={18} />
                </a>
              )}

              <small>
                {isAndroid
                  ? "Android app • Official KostPadi release"
                  : isIOS
                    ? "iPhone & iPad • Coming soon"
                    : "Web app • Android app available"}
              </small>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default DownloadApp;
